import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContainerSasUri, LoaderHandlingMode } from '@shared/models';
import { ForfettappApiClient, ForfettappApiClientOptions } from 'app/shared/forfettapp-api-client';
import { ForfettarioDataService } from 'app/shared/selezione-forfettario-folder/forfettario-data.service';
import ArrayStore from 'devextreme/data/array_store';
import { BehaviorSubject } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { ControlloCoerenzaViewModel } from '../emissioni-folder/emissioni-models';
import { DichiarazioneModel } from './dichiarazione/dischiarazione.models';
import { DichiarazioneService } from './dichiarazione/services/dichiarazione.service';
import { SetCciaReaPayload } from './edit-ccia-rea-modal/edit-ccia-rea-modal.component';
import {
    AccountData,
    AtecoUtente,
    ContatoriCriticitaUtente,
    DatiIva,
    DatiUltimaImportazione,
    descrizioniTipoClienteLookUp,
    EsitoDownloadCorrispettiviRt,
    Forfettario,
    ForfettarioListItem,
    NoyificaPushPayload,
    ProviderSdiId,
    Residenza,
    RettificaContribuzioneCommand,
    SetDatiInpsCommand,
    UpdateOpzioniImportazioneFatturePayload,
    UpdateOpzioniSincronizzazioneCorrispettiviPayload,
    Utente,
} from './forfettario.models';

export interface modalitaPagamento {
    id: number;
    codiceFatturaElettronica: string;
    descrizione: string;
    richiedeIban: boolean;
}

@Injectable({ providedIn: 'root' })
export class ForfettarioService {
    motivazioni = [
        { descrizione: 'Immagine troppo chiara', id: 0 },
        { descrizione: 'Immagine troppo scura', id: 1 },
        { descrizione: 'Immagine sfuocata', id: 2 },
        { descrizione: 'Immagine troppo inclinata', id: 3 },
        { descrizione: 'Documento di identità non valido: caricare Carta di Identità o Passaporto', id: 4 },
        { descrizione: 'Altro...', id: 5 },
    ];

    private _forfettarioTokenContainer = new BehaviorSubject<ContainerSasUri>(null);

    private _forfettarioAccountData = new BehaviorSubject<AccountData>(null);

    private forfettarioLogo = new BehaviorSubject<string>(null);

    get forfettarioTokenContainer$() {
        return this._forfettarioTokenContainer.asObservable().pipe(filter(containerSasUri => Boolean(containerSasUri)));
    }

    get forfettarioTokenContainer() {
        return this._forfettarioTokenContainer.value;
    }

    get forfettarioAccountData$() {
        return this._forfettarioAccountData.asObservable().pipe(filter(accountData => Boolean(accountData)));
    }

    get forfettarioAccountData() {
        return this._forfettarioAccountData.value;
    }

    get forfettarioLogo$() {
        return this.forfettarioLogo.asObservable().pipe(filter(x => Boolean(x)));
    }

    constructor(
        private service: ForfettappApiClient,
        private forfettarioDataService: ForfettarioDataService,
        private dichiarazioneService: DichiarazioneService,
    ) {}

    mapUtenteToForfettario(utenteId: number, x: Utente): Forfettario {
        return {
            id: utenteId,
            denominazione: x.intestazione.denominazione,
            cognome: x.cognome,
            nome: x.nome,
            codiceFiscale: x.codiceFiscale,
            partitaIVA: x.partitaIva,
            indirizzoEmail: x.recapiti.indirizzoEmail,
            indirizzoEmailRegistrazione: x.recapiti.indirizzoEmailAccount,
            dataScadenzaAbbonamento: x.dataScadenzaAbbonamento,
            sedeInpsId: x.datiContribuzione.sedeInpsId,
            codiceAziendaInps: x.datiContribuzione.codiceAziendaInps,
            sesso: x.sesso,
            dataNascita: x.dataNascita,
            provinciaNascita: x.comuneDiNascita.codiceProvinciaNascita,
            comuneNascita: x.comuneDiNascita.codiceComuneNascita,
            nazione: null,
            indirizzo: x.intestazione.indirizzo,
            cap: x.intestazione.cap,
            civico: null,
            citta: x.intestazione.citta,
            provincia: x.intestazione.provincia,
            indirizzoPEC: x.recapiti.indirizzoPec,
            numeroTelefono: x.recapiti.numeroTelefono,
            providerSdiToken: x.opzioniFattura.providerSdiToken,

            indirizzoResidenza: x.residenza.indirizzoResidenza,
            codiceProvinciaResidenza: x.residenza.codiceProvinciaResidenza,
            civicoResidenza: x.residenza.civicoResidenza,
            capResidenza: x.residenza.capResidenza,
            dugresidenza: x.residenza.dugresidenza,
            codiceCatastaleResidenza: x.residenza.codiceCatastaleComuneResidenza,

            dataInizioIva: x.datiIva.dataInizioIva,
            dataFineIva: x.datiIva.dataFineIva,

            fineTrimestreImpostaDiBollo: x.periodicita.fineTrimestreImpostaDiBollo,

            cciaa: x.cameraCommercio.cciaa,
            rea: x.cameraCommercio.rea,

            dataAbilitazioneCassettoFiscale: x.cassettoFiscale.dataAbilitazioneCassettoFiscale,
            dataFineAbilitazioneCassettoFiscale: x.cassettoFiscale.dataFineAbilitazioneCassettoFiscale,
            dataValidazioneCassettoFiscale: x.cassettoFiscale.dataValidazioneCassettoFiscale,
            dataRichiestaCassettoFiscale: x.cassettoFiscale.dataRichiestaCassettoFiscale,
            dataUltimoDownloadCassettoFiscale: x.cassettoFiscale.dataUltimoDownloadCassettoFiscale,
            cassettoFiscaleAffidabile: x.cassettoFiscale.cassettoFiscaleAffidabile,

            bolloInFattura: x.opzioniFattura.bolloInFattura,
            contributoPrevidenzialeInFattura: x.opzioniFattura.contributoPrevidenzialeInFattura,
            fatturazioneACorpo: x.opzioniFattura.fatturazioneACorpo,

            startUpNel: x.datiIva.startUpNel,

            fatturazioneSdiAttiva: x.opzioniFattura.fatturazioneSdiAttiva,

            cciaaTipoIscrizione: x.cameraCommercio.cciaatipoIscrizione,
            cciaaUnitaLocali: x.cameraCommercio.unitaLocali, // TODO

            isAbbonamentoAttivo: true, // TODO
            joinDate: null, // TODO
            codiceAffiliazione: x.codiceAffiliazione,

            registrazioneConSpid: x.registrazioneConSpid,

            inailCodiceSede: x.datiInail.inailCodiceSede,
            inailCodiceDitta: x.datiInail.inailCodiceDitta,
            inailCc: x.datiInail.inailCc,

            contribuzioniId: x.datiContribuzione.contribuzioniId,
            atecoUtente: x.atecoUtente,
            ibanPredefinito: x.pagamentoPredefinitoFattura.fattureIBAN,
            ibanF24: x.opzioniPagamentoF24.iban,
            opzioniPagamentoF24: x.opzioniPagamentoF24,
            opzioniFattura: x.opzioniFattura,
            opzioniCorrispettivo: x.opzioniCorrispettivo,
            luogoDiEsercizio: x.luogoDiEsercizio,
        } as Forfettario;
    }

    getAccountData(utenteId: number, clientOptions?: ForfettappApiClientOptions) {
        var header = new HttpHeaders();
        if (clientOptions?.cacheData) header = header.set('cache-data', String(clientOptions.cacheData));
        if (clientOptions?.refreshData) header = header.set('refresh-data', String(clientOptions.refreshData));

        return this.service
            .get<AccountData>({
                url: `/backoffice/forfettari/${utenteId}/account-data`,
                headers: header,
                responseType: 'json',
            })
            .pipe(tap(x => this._forfettarioAccountData.next(x)));
    }

    getUltimaImportazione(utenteId: number) {
        return this.service.get<DatiUltimaImportazione>(`/backoffice/forfettari/${utenteId}/ultima-importazione`);
    }

    getUltimoDownloadRt(utenteId: number) {
        return this.service.get<EsitoDownloadCorrispettiviRt>(`/backoffice/forfettari/${utenteId}/ultimo-download-rt`);
    }

    aggiornaOpzioniImportazioneFatture(utenteId: number, payload: UpdateOpzioniImportazioneFatturePayload) {
        return this.service.post(`/backoffice/forfettari/${utenteId}/fatturazione-elettronica/opzioni-importazione`, payload);
    }

    aggiornaOpzioniSincronizzazioneCorrispettivi(utenteId: number, payload: UpdateOpzioniSincronizzazioneCorrispettiviPayload) {
        return this.service.post(`/backoffice/forfettari/${utenteId}/corrispettivi/opzioni-sincronizzazione`, payload);
    }

    modificaDatiInps(utenteId: number, payload: SetDatiInpsCommand) {
        return this.service
            .post(`/backoffice/forfettari/${utenteId}/contribuzione/inps/${payload.anno}`, payload.datiContribuzione)
            .pipe(tap(() => this.dichiarazioneService.refreshDichiarazioneDeiRedditi()));
    }

    setDatiInps(utenteId: number, payload: SetDatiInpsCommand) {
        return this.service
            .post(`/backoffice/forfettari/${utenteId}/contribuzione/inps/${payload.anno}`, payload.datiContribuzione)
            .pipe(tap(() => this.dichiarazioneService.refreshDichiarazioneDeiRedditi()));
    }

    rettificaContribuzione({ utenteId, anno: annoRedditi }: DichiarazioneModel, rettificaContribuzioneCommand: RettificaContribuzioneCommand) {
        return this.service.post(`/backoffice/forfettari/${utenteId}/dichiarazione/${annoRedditi}/rettifica-contribuzione`, rettificaContribuzioneCommand).pipe(
            tap(() => this.dichiarazioneService.refreshDichiarazioneDeiRedditi()),
            switchMap(() => this.getForfettarioPerId(utenteId)),
            tap(forfettario => this.forfettarioDataService.sendUpsertEvent(forfettario)),
        );
    }

    getForfettarioPerId(utenteId: number) {
        return this.service.get<Utente>(`/backoffice/forfettari/${utenteId}`).pipe(map(x => this.mapUtenteToForfettario(utenteId, x)));
    }

    getForfettarioPerIdNoLoader(utenteId: number) {
        return this.service
            .get<Utente>(`/backoffice/forfettari/${utenteId}`, null, null, LoaderHandlingMode.useLocalLoaderHandler)
            .pipe(map(x => this.mapUtenteToForfettario(utenteId, x)));
    }

    getAtecoUtenteList(utenteId: number) {
        return this.service.get<AtecoUtente[]>(`/backoffice/forfettari/${utenteId}/ateco`);
    }

    sendNotificaPush(utenteId: number, payload: NoyificaPushPayload) {
        return this.service.post(`/backoffice/notifiche/notificapush/${utenteId}`, payload);
    }

    setIndirizzoPec(utenteId: number, indirizzoPec: string) {
        return this.service.post<Utente>(`/backoffice/forfettari/${utenteId}/recapiti/indirizzo-pec`, { IndirizzoPec: indirizzoPec }).pipe(
            map(x => this.mapUtenteToForfettario(utenteId, x)),
            tap(forfettario => this.forfettarioDataService.sendUpsertEvent(forfettario)),
        );
    }

    setNumeroTelefono(utenteId: number, numeroTelefono: string) {
        return this.service.post<Utente>(`/backoffice/forfettari/${utenteId}/recapiti/numero-telefono`, { NumeroTelefono: numeroTelefono }).pipe(
            map(x => this.mapUtenteToForfettario(utenteId, x)),
            tap(forfettario => this.forfettarioDataService.sendUpsertEvent(forfettario)),
        );
    }

    setfineTrimestreImpostaDiBollo(utenteId: number, dataPagamentoImpostaDiBollo: Date) {
        return this.service
            .post<Utente>(`/backoffice/forfettari/${utenteId}/data-pagamento-imposta-di-bollo`, {
                dataPagamentoImpostaDiBollo: dataPagamentoImpostaDiBollo,
            })
            .pipe(
                map(x => this.mapUtenteToForfettario(utenteId, x)),
                tap(forfettario => this.forfettarioDataService.sendUpsertEvent(forfettario)),
            );
    }

    attivaCriticitaAteco2025(utenteId: number) {
        return this.service.post(`/backoffice/forfettari/${utenteId}/ateco/commands/crea-criticita`, null);
    }

    getForfettarioIdPerCF(codiceFiscale: string) {
        return this.getFilteredForfettari(codiceFiscale).pipe(map(f => f[0].id));
    }

    getLogo(utenteId: number) {
        return this.service
            .get<string>({ url: `/backoffice/forfettari/${utenteId}/logo`, responseType: 'text' })
            .pipe(tap(logo => this.forfettarioLogo.next(logo)));
    }

    getCriticitaForfettario(codiceFiscale: string) {
        return this.service.get<ControlloCoerenzaViewModel[]>({ url: `/backoffice/criticita/${codiceFiscale}` });
    }

    getContatoriCriticitaForfettario(codiceFiscale: string) {
        return this.service.get<Array<ContatoriCriticitaUtente>>({ url: `/backoffice/criticita/${codiceFiscale}/contatori` });
    }

    updateLogo(utenteId: number, logo: File) {
        const formData: FormData = new FormData();
        formData.append('files', logo);

        return this.service.post(`/backoffice/forfettari/${utenteId}/logo`, formData);
    }

    updateResidenza(utenteId: number, residenza: Residenza) {
        return this.service.post(`/backoffice/forfettari/${utenteId}/residenza`, residenza);
    }

    rinnovaAbbonamentoAffiliato(utenteId: number, ticketId: string) {
        let params = new HttpParams();
        params = params.set('idUtente', utenteId.toString());
        params = params.set('ticketRiferimentoId', ticketId.toString());

        return this.service.post(`/backoffice/rinnova-abbonamento-affiliato`, null, params);
    }

    segnaOrdinePagatoEsternamente(ordineId: number, ticketId: string) {
        let params = new HttpParams();
        params = params.set('ordineId', ordineId.toString());
        params = params.set('ticketRiferimentoId', ticketId.toString());

        return this.service.post(`/backoffice/segna-ordine-pagato-esternamente`, null, params);
    }

    aggiungiAbbonamentoManualmente(utenteId: number, ticketId: string) {
        let params = new HttpParams();
        params = params.set('idUtente', utenteId.toString());
        params = params.set('ticketRiferimentoId', ticketId.toString());

        return this.service.post(`/backoffice/aggiungi-abbonamento-manualmente`, null, params);
    }
    modificaAffiliazioneUtente(utenteId: number, codiceAffiliazione: string, aggiornaEventualeUltimoOrdineAperto: boolean) {
        let params = new HttpParams();
        params = params.set('utentiId', utenteId.toString());
        params = params.set('codiceAffiliazione', codiceAffiliazione.toString());
        params = params.set('aggiornaEventualeUltimoOrdineAperto', aggiornaEventualeUltimoOrdineAperto.toString());

        return this.service.post(`/backoffice/forfettari/aggiorna-affiliazione-utente`, null, params);
    }

    getFilteredForfettari(ricerca: string) {
        console.warn('ricerca');
        let params = new HttpParams();
        params = params.append('ricerca', ricerca);
        return this.service.get<ForfettarioListItem[]>(`/backoffice/forfettari/forfettari-list`, params);
    }

    setStartupData(utenteId: number, datiIva: DatiIva, annotazioni: string) {
        let params = new HttpParams();
        params = params.append('annotazioni', annotazioni);

        return this.service
            .post<{ id: number }>(`/backoffice/forfettari/${utenteId}/dati-iva`, datiIva, params)
            .pipe(switchMap(x => this.getForfettarioPerId(x.id)));
    }

    setCciaRea(utenteId: number, cciaAndRea: SetCciaReaPayload) {
        return this.service.post<Utente>(`/backoffice/forfettari/${utenteId}/camera-commercio/ccia-rea`, cciaAndRea).pipe(
            map(x => this.mapUtenteToForfettario(utenteId, x)),
            tap(forfettario => this.forfettarioDataService.sendUpsertEvent(forfettario)),
        );
    }

    changeProviderFatturazioneElettronica(utenteId: number, providerSdiId: ProviderSdiId) {
        return this.service.post<Utente>(`/backoffice/forfettari/${utenteId}/fatturazione-elettronica/change-provider`, providerSdiId).pipe(
            map(x => this.mapUtenteToForfettario(utenteId, x)),
            tap(forfettario => this.forfettarioDataService.sendUpsertEvent(forfettario)),
        );
    }

    getCodiceDestinatarioProviderSdi(providerSdiId: ProviderSdiId) {
        return this.service.get<string>({
            url: `/backoffice/forfettari/fatturazione-elettronica/provider-sdi/${providerSdiId}/codice-destinatario`,
            responseType: 'text',
        });
    }

    aggiornaAnnotazioniUtente(utente: Forfettario, annotazioni: string, loaderHandling = LoaderHandlingMode.useGlobalLoaderHandler) {
        var httpParams = new HttpParams();
        httpParams = httpParams.set('annotazioni', annotazioni);
        return this.service
            .post(`/backoffice/forfettari/${utente.id}/commands/aggiorna-annotazioni-utente`, null, httpParams, null, loaderHandling)
            .pipe(tap(() => this.forfettarioDataService.sendUpsertEvent(utente)));
    }

    getAnnotazioniUtente(utenteId: number, loaderHandling = LoaderHandlingMode.useGlobalLoaderHandler) {
        return this.service.get<string>({ url: `/backoffice/forfettari/${utenteId}/commands/get-annotazioni-utente`, responseType: 'text', loaderHandling });
    }

    toggleServizioFatturazioneElettronica(utenteId: number, servizioAttivo: boolean) {
        const params = new HttpParams().append('servizioAttivo', servizioAttivo.toString());
        return this.service.post<Utente>(`/backoffice/forfettari/${utenteId}/fatturazione-elettronica/toggle-servizio`, {}, params).pipe(
            map(x => this.mapUtenteToForfettario(utenteId, x)),
            tap(forfettario => this.forfettarioDataService.sendUpsertEvent(forfettario)),
        );
    }

    deleteAteco(utenteId: number, idAteco: number) {
        return this.service.delete(`/backoffice/forfettari/v2/${utenteId}/ateco/${idAteco}`);
    }

    addAteco(utenteId: number, ateco: AtecoUtente) {
        return this.service.put<AtecoUtente>(`/backoffice/forfettari/${utenteId}/ateco`, ateco);
    }

    updateAteco(forfettario: Forfettario, ateco: AtecoUtente) {
        ateco.visibile ??= null;
        return this.service.post<any>(`/backoffice/forfettari/${forfettario.id}/ateco`, ateco).pipe(
            switchMap(() => this.getForfettarioPerId(forfettario.id)),
            tap(forfettario => this.forfettarioDataService.sendUpsertEvent(forfettario)),
        );
    }

    impostaUnitaLocali(utentiId: number, unitaLocali: string[]) {
        return this.service.post<Utente>(`/backoffice/forfettari/${utentiId}/unita-locali`, unitaLocali).pipe(
            map(x => this.mapUtenteToForfettario(utentiId, x)),
            tap(forfettario => this.forfettarioDataService.sendUpsertEvent(forfettario)),
        );
    }

    getTokenContainerUtente(utenteId: number, clientOptions?: ForfettappApiClientOptions) {
        var header = new HttpHeaders();
        if (clientOptions?.cacheData) header = header.set('cache-data', String(clientOptions.cacheData));
        if (clientOptions?.refreshData) header = header.set('refresh-data', String(clientOptions.refreshData));

        return this.service
            .get<ContainerSasUri>({
                url: `/backoffice/forfettari/${utenteId}/sas-token`,
                headers: header,
            })
            .pipe(tap(tokenUtente => this._forfettarioTokenContainer.next(tokenUtente)));
    }
}

export const TipoClienteLookUp = new ArrayStore({
    data: descrizioniTipoClienteLookUp,
    key: 'value',
});
